import { ref } from 'vue';
import Pusher from 'pusher-js/with-encryption';
import useAdobeService from '@/services/adobe';
import { PUSHER_KEY, PUSHER_CLUSTER } from '@/config/appConfig';
import shouldRetryFetch from '@/utils/shouldRetryFetch';

/* ***** User Channels ***** */

const pusherInstance = ref(null); // Generic connection

function initPusher() {
  pusherInstance.value = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
  return pusherInstance.value;
}

function subscribeToUserChannel(userId) {
  if (!userId) return undefined;
  if (!pusherInstance.value) initPusher();
  return pusherInstance.value.subscribe(`user-${userId}`);
}

function unsubscribeFromUserChannel(userId) {
  if (!userId) return undefined;
  if (!pusherInstance.value) initPusher();
  return pusherInstance.value.unsubscribe(`user-${userId}`);
}

/* ***** Adobe Channels ***** */

const pusherInstanceAdobe = ref(null); // Authed connection for encrypted Adobe notifications.

const makePusherAuth = async (pusherPayload, retryCount) => useAdobeService()
  .makePusherAuth(pusherPayload, {
    async onFetchError(error) {
    // retry if the error is a network error or a 5xx server error
      if (shouldRetryFetch(error)) {
        if (retryCount > 0) {
          setTimeout(async () => {
            await makePusherAuth(pusherPayload, retryCount - 1);
          }, 1000); // Throttle by 1 second

          return error;
        }
      }

      // because we are outside of the component when this function is called,
      // we don't have access to Toast component
      const { default: useToastsStore } = await import('@/stores/toasts');
      const { addToToastQueue } = useToastsStore();

      addToToastQueue({
        severity: 'error',
        detail: 'There is an issue with the Adobe PSD upload service. The feature may not work as expected. Please refresh or try again later.',
        life: 5000,
      });

      return error;
    },
  });

const adobeCustomHandler = (userId) => async ({ channelName, socketId }, cb) => {
  const pusherPayload = {
    userId,
    channelName,
    socketId,
  };

  const {
    data: pusherAuthData,
    error: pusherAuthError,
  } = await makePusherAuth(pusherPayload, 3);
  cb(pusherAuthError?.value, pusherAuthData?.value);
};

function initAdobePusher(userId) {
  const customHandler = adobeCustomHandler(userId);

  pusherInstanceAdobe.value = new Pusher(PUSHER_KEY, {
    cluster: PUSHER_CLUSTER,
    enabledTransports: ['wss', 'ws', 'xhr_streaming'],
    channelAuthorization: { customHandler },
    userAuthentication: { customHandler },
  });

  return pusherInstanceAdobe.value.signin();
}

function subscribeToAdobeNotifications(userId) {
  if (!userId) return undefined;
  return pusherInstanceAdobe.value.subscribe(`private-encrypted-notifications_${userId}`);
}

function unsubscribeFromAdobeNotifications(userId) {
  if (!userId) return undefined;
  return pusherInstanceAdobe.value.unsubscribe(`private-encrypted-notifications_${userId}`);
}

/* ***** Exports ***** */

export default () => {
  const init = async (userId) => {
    const user = initPusher();
    const adobe = await initAdobePusher(userId);

    return {
      adobe,
      user,
    };
  };

  return {
    instances: {
      adobe: pusherInstanceAdobe?.value || pusherInstanceAdobe,
      user: pusherInstance?.value || pusherInstance,
    },
    init,
    subscribeToAdobeNotifications,
    unsubscribeFromAdobeNotifications,
    subscribeToUserChannel,
    unsubscribeFromUserChannel,
  };
};
